import { hash } from '@ember/helper';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';

import can from 'ember-can/helpers/can';
import CpPanel from 'ember-collapsible-panel/components/cp-panel/component';
import { t } from 'ember-intl';
import momentFormat from 'ember-moment/helpers/moment-format';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and, not } from 'ember-truth-helpers';

import Component from 'umg-ember-backend/components/record-detail/forms';
import AssetPreview from 'umg-ember-backend/components/ui/asset-preview';
import Form from 'umg-ember-backend/components/ui/form';
import InfoBox from 'umg-ember-backend/components/ui/info-box';
import plus from 'umg-ember-backend/helpers/plus';

export default class RecordDetailFormsArtistComponent extends Component {
  // Services
  @service data;
  @service flashMessages;
  @service filter;
  @service store;

  // Defaults
  artistTypes = [
    { value: 'contributor', label: 'Contributor' },
    { value: 'serie', label: 'Serie' },
    { value: 'project', label: 'Project' },
  ];

  // Getter and setter
  get showUrlAliasWarning() {
    return (
      this.args.model.channels.length && !this.args.model.urlAliases.length
    );
  }

  // Functions
  getHint = (category) => {
    let hint;

    switch (category.get('id')) {
      case '3826':
        hint = 'https://www.tiktok.com/@<strong>&lt;username&gt;</strong>';
        break;
      case '3269':
        hint = 'https://www.instagram.com/<strong>&lt;profilname&gt;</strong>';
        break;
      case '2313':
        hint = 'https://twitter.com/<strong>&lt;profilname&gt;</strong>';
        break;
      case '4686':
        hint = 'https://www.youtube.com/<strong>&lt;username&gt;</strong>';
        break;
      case '3268':
        hint =
          'https://www.youtube.com/channel/<strong>&lt;channel_id&gt;</strong>';
        break;
      case '2616':
        hint =
          'https://open.spotify.com/<strong>artist/&lt;Embed-ID&gt;</strong>';
        break;
      case '2564':
        hint =
          'https://music.apple.com/<strong>de/artist/&lt;künstlername&gt;/&lt;Embed-ID&gt;</strong>';
        break;
      case '4293':
        hint =
          'https://www.deezer.com/<strong>de/artist/&lt;artist-ID&gt;</strong>';
        break;
      case '2298':
        hint = 'https://www.facebook.com/<strong>&lt;username&gt;</strong>';
        break;
      case '4448':
        hint = 'https://www.pinterest.de/<strong>&lt;profilname&gt;</strong>';
        break;
      case '3833':
        hint = '<strong>&lt;Shop-URL&gt;</strong>';
        break;
      case '4295':
        hint = '<strong>&lt;email&gt;</strong>';
        break;
      case '3316':
        hint = 'https://soundcloud.com/<strong>&lt;profilname&gt;</strong>';
        break;
      case '2607':
        hint =
          'https://open.spotify.com/intl-de/<strong>&lt;album/track/artist-ID&gt;</strong>';
        break;
      default:
        hint = '';
        break;
    }

    return htmlSafe(hint);
  };

  // Template
  <template>
    <Form @model={{@model}} @showErrors={{@showErrors}} as |form|>
      {{#if this.showUrlAliasWarning}}
        <InfoBox @type='warning'>
          {{t 'record_detail.forms.artist.no_url_alias'}}
        </InfoBox>
      {{/if}}

      <div class='o-layout'>
        <div class='o-layout__item u-1/1 u-1/2@tablet'>
          <form.sections.variant
            @availableLanguages={{this.availableLanguages}}
          />

          <form.fieldset @titleKey='general' as |fieldset|>
            <fieldset.field @property='name' />
            <fieldset.field @property='screenname' />

            <fieldset.field
              @property='themeType'
              @type='select-value'
              @options={{this.artistTypes}}
            />

            <fieldset.field
              @property='cmsState'
              @type='select-value'
              @options={{this.cmsStateOptions}}
            />

            {{#if this.isParent}}
              <fieldset.field
                @property='cmsLanguageId'
                @type='select-value'
                @options={{this.availableLanguages}}
              />
            {{/if}}

            <fieldset.field @property='dateBirth' @type='date' />
            <fieldset.field @property='dateDeath' @type='date' />

            <fieldset.field @property='text' @type='text-editor' />

            <fieldset.field
              @filters={{hash assetFolderId='2'}}
              @property='assetIdImage'
              @options={{hash allowedFolder='100'}}
              @type='belongs-to'
              as |record|
            >
              <AssetPreview @asset={{record}} />
            </fieldset.field>

            <fieldset.field
              @filters={{hash assetFolderId='4'}}
              @property='assetIdEyecatcher'
              @options={{hash allowedFolder='100'}}
              @type='belongs-to'
              as |record|
            >
              <AssetPreview @asset={{record}} />
            </fieldset.field>

            <fieldset.field
              @filters={{hash assetFolderId='4'}}
              @property='promotionImage'
              @options={{hash allowedFolder='100'}}
              @type='belongs-to'
              as |record|
            >
              <AssetPreview @asset={{record}} />
            </fieldset.field>

            <fieldset.field @property='colorArtist' @type='color' />
          </form.fieldset>

          <form.fieldset @titleKey='controls' as |fieldset|>
            <fieldset.field @property='showHome' @type='checkbox' />

            <fieldset.field
              @property='showSpotifyPlaylistGenerator'
              @type='checkbox'
            />

            <fieldset.field @property='visibleInList' @type='checkbox' />
          </form.fieldset>

          <form.fieldset @titleKey='newsletterConfig' as |fieldset|>
            <fieldset.field @property='showNwlt' @type='checkbox' />
            <fieldset.field
              @property='newsletterConfigs'
              @type='has-many'
              @filters={{hash artist=@model}}
              @on={{hash openRecordList=false removeRecord=this.deleteRelation}}
            />
          </form.fieldset>
        </div>
        <div class='o-layout__item u-1/1 u-1/2@tablet'>
          <form.fieldset @titleKey='assignments' as |fieldset|>
            <fieldset.field
              @property='externalReferences'
              @type='has-many'
              @hasManySortable={{true}}
              @on={{hash
                openNewRecord=this.newRecord
                openRecord=false
                removeRecord=this.deleteRelation
                openRecordList=false
              }}
              as |record field|
            >
              <CpPanel @open={{record.isNew}} as |panel|>
                <panel.toggle>
                  {{record.type.name}}
                  |
                  {{record.val}}
                </panel.toggle>
                <panel.body>
                  <Form
                    @model={{record}}
                    @parentModel={{form.model}}
                    @showErrors={{@showErrors}}
                    as |subform|
                  >
                    <subform.field
                      @property='type'
                      @type='select-record'
                      @disabled={{field.isDisabled}}
                      @isSearchEnabled={{true}}
                      @options={{this.data.externalReferenceServices}}
                    />
                    <subform.field
                      @property='val'
                      @disabled={{field.isDisabled}}
                      @hint={{this.getHint record.type}}
                    />
                    <subform.field
                      @property='param'
                      @disabled={{field.isDisabled}}
                    />
                  </Form>
                </panel.body>
              </CpPanel>
            </fieldset.field>

            <fieldset.field
              @property='channels'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />

            <fieldset.field
              @property='ownedByChannel'
              @type='belongs-to'
              @on={{hash openRecord=false openNewRecord=false}}
            />

            <fieldset.field
              @property='divisions'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />

            <fieldset.field
              @property='labels'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />

            <fieldset.field
              @property='similarArtists'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />

            <fieldset.field
              @filters={{hash type='SUBGENRE'}}
              @property='categories'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />

            <fieldset.field
              @property='alternativeLabels'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />

            <fieldset.field
              @property='contactPeople'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />

            <fieldset.field
              @property='stories'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />
          </form.fieldset>

          <form.fieldset @titleKey='taxonomies' as |fieldset|>
            <fieldset.field @property='taxa' @type='has-many' />
          </form.fieldset>

          {{#unless this.isVariant}}
            <form.fieldset @titleKey='artistFunctions' as |fieldset|>
              <fieldset.field
                @filters={{hash type='CONTRIB_FUNC'}}
                @property='artistFunctions'
                @type='has-many-join'
                @hasManyForeignModelName='category'
                @on={{hash
                  openNewRecord=false
                  removeRecord=this.deleteRelation
                  openRecord=false
                }}
                as |record field|
              >
                <CpPanel @open={{record.isNew}} as |panel|>
                  <panel.toggle>
                    {{svgJar
                      (if record.mainFunction 'star' 'star-outline')
                      class='u-icon'
                    }}
                    {{record.category.name}}
                  </panel.toggle>
                  <panel.body>
                    <Form
                      @model={{record}}
                      @parentModel={{form.model}}
                      as |subform|
                    >
                      <subform.field
                        @property='mainFunction'
                        @type='checkbox'
                        @disabled={{field.isDisabled}}
                      />
                    </Form>
                  </panel.body>
                </CpPanel>
              </fieldset.field>
            </form.fieldset>
          {{/unless}}

          <form.fieldset @titleKey='discography' as |fieldset|>
            <fieldset.field
              @property='products'
              @type='has-many'
              @on={{hash openNewRecord=false}}
              as |record field index|
            >
              {{plus index 1}}.
              {{record.displayLabel}}
            </fieldset.field>
          </form.fieldset>

          <form.fieldset @titleKey='collections' as |fieldset|>
            <fieldset.field
              @property='collections'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />
          </form.fieldset>

          <form.fieldset @titleKey='urlAliases' as |fieldset|>
            <fieldset.field
              @property='urlAliases'
              @type='has-many'
              as |urlAlias|
            >
              {{svgJar
                (if
                  (and (not urlAlias.parent) (not urlAlias.isNew))
                  'star'
                  'star-outline'
                )
                class='u-icon'
              }}
              {{urlAlias.displayLabel}}
            </fieldset.field>
          </form.fieldset>

          <form.fieldset
            @titleKey='mmcRms'
            @isVisible={{and this.isParent (can 'viewMmcRms' @model)}}
          >
            {{#each @model.mmcIds.items as |item|}}
              {{t 'forms.artist.fields.mmcId'}}:
              {{item.mmcId}}<br />
            {{/each}}
            <br />
            {{#each @model.rmsIds.items as |item|}}
              {{t 'forms.artist.fields.rmsId'}}:
              {{item.rmsId}}<br />
            {{/each}}
          </form.fieldset>

          <form.fieldset @titleKey='keywords' as |fieldset|>
            <fieldset.field @property='keywords' />
          </form.fieldset>

          <form.sections.seo />

          <form.sections.meta>
            <label>
              {{t
                'record_detail.forms.artist.meta_box.labels.cmsContentModified'
              }}
            </label>
            <div class='u-margin-bottom-small'>
              {{momentFormat @model.cmsContentModified 'L LT'}}
            </div>
          </form.sections.meta>
        </div>
      </div>
    </Form>
  </template>
}
